import * as React from 'react';

import { CSSObject, Theme, useTheme } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';

import Box from '@mui/material/Box';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CssBaseline from '@mui/material/CssBaseline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Divider from '@mui/material/Divider';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import LayersIcon from '@mui/icons-material/Layers';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import MenuIcon from '@mui/icons-material/Menu';
import MuiDrawer from '@mui/material/Drawer';
import NotificationsView from '../NotificationsView';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { appVersion } from '../../utils/version';
import { defaultThemeColor } from '../../config/themeConfig';
import { useKeycloak, useAuthSafe } from '../ConditionalAuthProvider';
import { useCapabilityAccess } from '../../hooks/useCapabilityAccess';
import { isKeycloakEnabled } from '../../config/authConfig';
import { logoutUser } from '../../redux/slices/userSlice'; // Adjust the import according to your file structure
import { clearActionTrackingState } from '../../redux/slices/actionsSlice';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { closeAllRtuWebSessions } from '../../services/rtuWebGatewaySessions';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

// Define the version of your app
// const appVersion = '2.1.31'; // Replace with your actual version

export default function Sidebar() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [allAssetsOpen, setAllAssetsOpen] = React.useState(false); // State to manage "All Assets" submenu
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();  // Add this line to use the translation hook
  const { keycloak } = useKeycloak();
  const { logout: backendLogout } = useAuthSafe();
  const capabilityAccess = useCapabilityAccess();

  // const handleLogout = () => {
  //   dispatch(logoutUser()); // Dispatch a logout action to update the Redux store
  //   navigate('/login'); // Navigate the user to the login page
  // };

  const handleLogout = async () => {
    // Clear local Redux user state
    dispatch(clearActionTrackingState());
    dispatch(logoutUser());

    // End the RTU web sessions opened from this tab while the outgoing token is still valid (best effort, never blocks).
    closeAllRtuWebSessions('DMS_SIGNED_OUT', keycloak?.token);

    if (isKeycloakEnabled()) {
      // Keycloak mode
      if (keycloak) {
        try {
          await keycloak.logout({ redirectUri: window.location.origin });
        } catch (e) {
          // Fall back to app logout
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
    } else {
      // Backend auth mode
      try {
        await backendLogout();
      } finally {
        navigate('/login');
      }
    }
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setAllAssetsOpen(false); 
  };

  const menuItems = [
    { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('navigation.assets'), icon: <LayersIcon />, path: '', subItems: [
      { text: t('navigation.groupedAssets'), icon: <LayersIcon />, path: '/rtutreelist' },
      { text: t('navigation.assetList'), icon: <LayersIcon />, path: '/rtulist' },
      ...(FEATURE_FLAGS.mapView && (!FEATURE_FLAGS.mapViewRequiresBeta || capabilityAccess.canAccessBetaFeatures)
        ? [{ text: t('navigation.mapView'), icon: <MapIcon />, path: '/mapview' }]
        : []),
    ]},
    // Data Reception is a normal Operator surface; internal observability access
    // (support staff without a DMS group role) can still reach it too.
    ...(FEATURE_FLAGS.ingestionObservability
      && (capabilityAccess.canUseDmsOperatorFeatures || capabilityAccess.canAccessInternalObservability)
      ? [{ text: t('observability.navTitle'), icon: <QueryStatsIcon />, path: '/observability/ingestion' }]
      : []),
    // Action history/artifacts are a read surface available to Operator and
    // Administrator alike; only launching/retrying/cancelling is Administrator-only.
    ...(FEATURE_FLAGS.actionsTracking && capabilityAccess.canUseDmsOperatorFeatures
      ? [
          { text: t('actionsWorkbench.title', { defaultValue: 'All Actions' }), icon: <HistoryIcon />, path: '/actions' },
          { text: t('audit.report.title', { defaultValue: 'Reports' }), icon: <FactCheckIcon />, path: '/audit/report' },
        ]
      : [])
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar style={{ backgroundColor: defaultThemeColor, borderColor: defaultThemeColor }} position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label={t('open drawer')}  // Translate the aria-label
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {t('Intelevate® Edge Devices Management System')}  
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            {/* Keep notification entry point disabled until notification flows are implemented end-to-end. */}
            {FEATURE_FLAGS.showNotifications ? <NotificationsView /> : null}

            <Tooltip title={t('logout')} arrow>
              <IconButton color="inherit" onClick={handleLogout} aria-label={t('logout')}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="inherit">
              {t('logout')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List sx={{ flexGrow: 1 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding sx={{ display: 'block' }}>
                <Tooltip title={item.text} placement="right" arrow>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                    onClick={() => {
                      // If the sidebar is closed, open both the sidebar and the "All Assets" submenu
                      if (!open && item.subItems) {
                        setOpen(true);
                        setAllAssetsOpen(true);
                      }
                      // If the sidebar is open and the item has subItems, toggle the "All Assets" submenu
                      else if (open && item.subItems) {
                        setAllAssetsOpen(!allAssetsOpen);
                      }
                      // If there are no subItems, navigate to the item's path
                      else if (!item.subItems) {
                        navigate(item.path);
                        setOpen(false); 
                        setAllAssetsOpen(false)// Close the sidebar when a sub-item is clicked
                      }
                    }}        
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
                    {item.subItems && open && (allAssetsOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />)}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {item.subItems && allAssetsOpen && (
                item.subItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding sx={{ display: 'block', pl: 4 }}>
                    <Tooltip title={subItem.text} placement="right" arrow>
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                        }}
                        onClick={() => {
                          navigate(subItem.path);
                          setOpen(false); // Close the sidebar when a sub-item is clicked
                          setAllAssetsOpen(false);
                        }}                    
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.text} sx={{ opacity: open ? 1 : 0 }} />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                ))
              )}
            </React.Fragment>
          ))}
        </List>
        <Divider />
        <Box sx={{ mt: 'auto', p: 2, textAlign: 'center', width: '100%' }}>
          <Typography variant="body2" color="textSecondary" noWrap>
            Version {appVersion}
          </Typography>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        {/* Your main content goes here */}
      </Box>
    </Box>
  );
}
